import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  ScatterChart, Scatter, ComposedChart, LabelList,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { METRICS, SERIES_DATA, MONTHS, TOP_LIST, ACTIVITY_LIST } from '../../data/catalog';
import ConsolidatedTable from './ConsolidatedTable';

const Tip = ({ active, payload, label }) => {
  if (!active||!payload?.length) return null;
  return (
    <div style={{ background:'#fff',border:'1px solid #e8e8e4',borderRadius:8,padding:'7px 11px',fontSize:12,boxShadow:'0 4px 14px rgba(0,0,0,0.1)' }}>
      <p style={{ color:'#6b6b66',marginBottom:3 }}>{label}</p>
      {payload.map(p=>(
        <p key={p.name} style={{ color:p.color||p.stroke,fontWeight:500 }}>
          {formatMetricNumber(p.value)}
        </p>
      ))}
    </div>
  );
};

const pickFirst = (...values) => values.find(value => value !== undefined && value !== null && value !== '');

const parseMetricNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = !trimmed.includes('.') && (trimmed.match(/,/g) || []).length === 1
    ? trimmed.replace(',', '.')
    : trimmed.replace(/,/g, '');
  const parsed = Number(normalized.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatMetricNumber = (value) => {
  const numeric = parseMetricNumber(value);
  if (numeric === null) return value ?? '';

  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatValue = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'number') return formatMetricNumber(value);
  return value;
};

const formatCompactValue = (value) => {
  return formatMetricNumber(value);
};

const normalizeSeries = (raw) => {
  if (!Array.isArray(raw)) return null;
  if (raw.length === 0) return null;

  const series = raw.map((item, index) => {
    if (typeof item === 'number') {
      return { mes: MONTHS[index] || String(index + 1), v: item };
    }

    if (typeof item !== 'object' || item === null) {
      const parsed = parseMetricNumber(item);
      return parsed === null ? null : { mes: MONTHS[index] || String(index + 1), v: parsed };
    }

    const rawValue = pickFirst(item.v, item.value, item.total, item.amount, item.count, item.y, 0);
    const value = parseMetricNumber(rawValue);
    if (value === null) return null;

    return {
      ...item,
      mes: pickFirst(item.mes, item.label, item.name, item.date, item.period, item.month, item.x, MONTHS[index], String(index + 1)),
      v: value,
    };
  }).filter(Boolean);

  return series.length ? series : null;
};

const normalizeItems = (raw) => {
  if (!Array.isArray(raw)) return null;
  if (raw.length === 0) return null;

  const items = raw.map((item, index) => {
    if (typeof item !== 'object' || item === null) {
      const numericValue = parseMetricNumber(item);
      return { name: String(item), value: item, numericValue, pct: null };
    }

    const value = pickFirst(item.value, item.total, item.amount, item.count, item.v, '');
    const numericValue = parseMetricNumber(value);
    return {
      ...item,
      name: pickFirst(item.name, item.label, item.title, `Item ${index + 1}`),
      value,
      numericValue,
      pct: parseMetricNumber(pickFirst(item.pct, item.percent, item.percentage)),
      time: pickFirst(item.time, item.date, item.period, ''),
      action: pickFirst(item.action, item.description, item.detail, ''),
      initials: pickFirst(item.initials, item.name?.slice(0, 2)?.toUpperCase(), 'SP'),
      color: item.color,
    };
  });

  const max = Math.max(...items.map(item => item.numericValue ?? 0), 0);
  return items.map(item => ({
    ...item,
    pct: item.pct ?? (max > 0 && item.numericValue !== null ? Math.max(4, Math.round((item.numericValue / max) * 100)) : 100),
  }));
};

const resolveMetricData = (metric, data) => {
  const source = data?.metric ?? data?.payload ?? data;
  if (source !== undefined && source !== null && typeof source !== 'object') {
    return {
      ...metric,
      value: formatValue(source, metric.value),
      hasRealData: true,
    };
  }

  if (Array.isArray(source)) {
    return {
      ...metric,
      series: normalizeSeries(source) || metric.series,
      items: normalizeItems(source) || metric.items,
      hasRealData: true,
    };
  }

  if (!source || typeof source !== 'object') return metric;

  const series = normalizeSeries(source.series ?? source.chartData ?? source.points ?? source.data);
  const items = normalizeItems(source.items ?? source.list ?? source.rows ?? source.data);
  const rawDelta = pickFirst(source.delta, source.change, source.variation, metric.delta);
  const rawUp = pickFirst(source.up, source.is_up, source.trend_up, source.trend === 'up' ? true : undefined, metric.up);

  return {
    ...metric,
    label: pickFirst(source.label, source.title, metric.label),
    color: pickFirst(source.color, metric.color),
    bg: pickFirst(source.bg, source.background, metric.bg),
    value: formatValue(pickFirst(source.formatted_value, source.value, source.total, source.current, source.amount, source.count), metric.value),
    unit: pickFirst(source.unit, metric.unit),
    hasRealData: true,
    delta: rawDelta,
    up: rawUp,
    sub: pickFirst(source.sub, source.subtitle, source.description, metric.sub),
    series: series || metric.series,
    items: items || metric.items,
  };
};

const getData = (metric) => {
  if (metric.series?.length) return metric.series;
  if (metric.hasRealData) {
    const value = parseMetricNumber(metric.value);
    return value === null ? [] : [{ mes: metric.label, v: value }];
  }
  return MONTHS.map((mes,i)=>({ mes, v: SERIES_DATA[metric.id]?.[i] ?? 50+i*5 }));
};

function SummaryWidget({ metric }) {
  return (
    <div style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:6,textAlign:'center' }}>
      <div style={{ fontSize:26,fontWeight:600,color:metric.color,lineHeight:1 }}>{metric.value}</div>
      {metric.sub&&<div style={{ fontSize:11,color:'#9f9f98',maxWidth:180 }}>{metric.sub}</div>}
    </div>
  );
}

function KpiWidget({ metric }) {
  const series = getData(metric).map(item => item.v).filter(value => typeof value === 'number');
  const values = series.length ? series : (SERIES_DATA[metric.id]||[50,60,55,70,65,80]);
  const max = Math.max(...values) || 1;
  return (
    <>
      <div style={{ fontSize:26,fontWeight:600,letterSpacing:'-.04em',color:metric.color,lineHeight:1,marginBottom:5 }}>{metric.value}</div>
      {metric.delta&&<span style={{ display:'inline-flex',alignItems:'center',gap:3,fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:20,background:metric.up?'#f0fdf4':'#fef2f2',color:metric.up?'#16a34a':'#dc2626',marginBottom:4 }}>{metric.up?'↑':'↓'} {metric.delta}</span>}
      <div style={{ fontSize:11,color:'#9f9f98',marginBottom:8 }}>{metric.sub}</div>
      <div style={{ display:'flex',alignItems:'flex-end',gap:2,flex:1 }}>
        {values.map((v,i)=><div key={i} style={{ flex:1,borderRadius:'2px 2px 0 0',minHeight:3,height:`${Math.round(v/max*100)}%`,background:i===values.length-1?metric.color:'#ededea',transition:'height .8s' }}/>)}
      </div>
    </>
  );
}

function StatDeltaWidget({ metric }) {
  if (metric.items?.length) {
    const topItems = metric.items.slice(0, 4);
    return (
      <>
        <div style={{ display:'flex',alignItems:'baseline',gap:7,marginBottom:8 }}>
          <div style={{ fontSize:26,fontWeight:600,letterSpacing:'-.03em',color:metric.color,lineHeight:1 }}>{metric.value}</div>
          <span style={{ fontSize:11,color:'#9f9f98' }}>{metric.unit === 'count' ? 'registros' : metric.unit}</span>
        </div>
        <div style={{ flex:1,display:'flex',flexDirection:'column',justifyContent:'center',gap:7,minHeight:0 }}>
          {topItems.map((item, index) => (
            <div key={`${item.name}-${index}`} style={{ display:'grid',gridTemplateColumns:'minmax(70px,120px) 1fr auto',alignItems:'center',gap:8 }}>
              <span style={{ fontSize:10,color:'#6b6b66',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{item.name}</span>
              <div style={{ height:7,background:'#f0f0ee',borderRadius:4,overflow:'hidden' }}>
                <div style={{ height:'100%',width:`${item.pct}%`,background:metric.color,borderRadius:4,opacity:.82 }}/>
              </div>
              <span style={{ fontSize:10,fontWeight:600,color:metric.color,minWidth:26,textAlign:'right' }}>{formatCompactValue(item.value)}</span>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ fontSize:24,fontWeight:600,letterSpacing:'-.03em',color:metric.color,lineHeight:1,marginBottom:6 }}>{metric.value}</div>
      {metric.delta&&<span style={{ display:'inline-flex',alignItems:'center',gap:3,fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:20,background:metric.up?'#f0fdf4':'#fef2f2',color:metric.up?'#16a34a':'#dc2626',marginBottom:8 }}>{metric.up?'↑':'↓'} {metric.delta} vs período anterior</span>}
      <div style={{ flex:1,display:'flex',flexDirection:'column',justifyContent:'flex-end',gap:4 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:11,color:'#9f9f98',marginBottom:3 }}>
          <span>Anterior</span><span>Actual</span>
        </div>
        <div style={{ height:6,background:'#f0f0ee',borderRadius:3,overflow:'hidden',position:'relative' }}>
          <div style={{ position:'absolute',left:0,top:0,height:'100%',width:'88%',background:'#e8e8e4',borderRadius:3 }}/>
          <div style={{ position:'absolute',left:0,top:0,height:'100%',width:'100%',background:metric.color,borderRadius:3 }}/>
        </div>
      </div>
    </>
  );
}

function BarWidget({ metric }) {
  return <div style={{ flex:1,minHeight:0 }}><ResponsiveContainer width="100%" height="100%"><BarChart data={getData(metric)} margin={{ top:2,right:2,bottom:0,left:-26 }}><XAxis dataKey="mes" tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><YAxis tickFormatter={formatMetricNumber} tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><Tooltip content={<Tip/>} cursor={{ fill:'rgba(80,70,228,0.05)' }}/><Bar dataKey="v" fill={metric.color} radius={[3,3,0,0]} opacity={.85}>{getData(metric).length <= 12 && <LabelList dataKey="v" position="top" {...valueLabelProps}/>}</Bar></BarChart></ResponsiveContainer></div>;
}

// Etiqueta con el valor de cada barra o punto, visible sin pasar el mouse
const valueLabelProps = { formatter: formatMetricNumber, fill: '#1a1a18', fontSize: 10, fontWeight: 600 };

/* Las listas «Por grupo» y «Por evento» van bajo el gráfico de ventas por sucursal. Cuando existen, la tarjeta crece para
   alojarlas (ver SlotCell) y el gráfico conserva su altura en vez de quedar aplastado. */
export const hasBreakdownLists = (metricId, data) => metricId === 'ventas_por_sucursal'
  && ((Array.isArray(data?.groups) && data.groups.length > 0) || (Array.isArray(data?.events) && data.events.length > 0));

const BAR_ROW_HEIGHT = 34;
const BAR_AXIS_HEIGHT = 40;
const BAR_MIN_CHART_HEIGHT = 130;

function BarHWidget({ metric, growWithLists = false }) {
  const data = getData(metric).slice(0, 6).map(d=>({ name:d.mes, v:d.v }));
  const chartHeight = Math.max(BAR_MIN_CHART_HEIGHT, data.length * BAR_ROW_HEIGHT + BAR_AXIS_HEIGHT);
  return (
    <div data-testid="bar-h-chart" style={growWithLists ? { flex:'0 0 auto',height:chartHeight } : { flex:1,minHeight:0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart layout="vertical" data={data} margin={{ top:2,right:56,bottom:0,left:8 }}>
          <XAxis type="number" tickFormatter={formatMetricNumber} tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/>
          <YAxis type="category" dataKey="name" tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false} width={84}/>
          <Tooltip content={<Tip/>} cursor={{ fill:'rgba(80,70,228,0.05)' }}/>
          <Bar dataKey="v" fill={metric.color} radius={[0,3,3,0]} opacity={.85}>
            <LabelList dataKey="v" position="right" {...valueLabelProps}/>
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LineWidget({ metric }) {
  const data = getData(metric);
  if (data.length <= 1) {
    const point = data[0];
    return (
      <div style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:12 }}>
        <div style={{ fontSize:28,fontWeight:600,color:metric.color,lineHeight:1 }}>{formatCompactValue(point?.v ?? metric.value)}</div>
        <div>
          <div style={{ fontSize:11,fontWeight:600,color:'#1a1a18' }}>{point?.mes || metric.label}</div>
          <div style={{ fontSize:10,color:'#9f9f98',marginTop:2 }}>{metric.unit === 'count' ? 'registros' : metric.unit || 'valor'}</div>
        </div>
      </div>
    );
  }

  return <div style={{ flex:1,minHeight:0 }}><ResponsiveContainer width="100%" height="100%"><LineChart data={data} margin={{ top:2,right:4,bottom:0,left:-26 }}><XAxis dataKey="mes" tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><YAxis tickFormatter={formatMetricNumber} tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><Tooltip content={<Tip/>}/><Line type="monotone" dataKey="v" stroke={metric.color} strokeWidth={2} dot={false}/></LineChart></ResponsiveContainer></div>;
}

function AreaWidget({ metric }) {
  const gId = `ag_${metric.id}`;
  const data = getData(metric);
  if (data.length <= 1) return <LineWidget metric={metric} />;

  return <div style={{ flex:1,minHeight:0 }}><ResponsiveContainer width="100%" height="100%"><AreaChart data={data} margin={{ top:2,right:4,bottom:0,left:-26 }}><defs><linearGradient id={gId} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={metric.color} stopOpacity={.12}/><stop offset="95%" stopColor={metric.color} stopOpacity={0}/></linearGradient></defs><XAxis dataKey="mes" tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><YAxis tickFormatter={formatMetricNumber} tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><Tooltip content={<Tip/>}/><Area type="monotone" dataKey="v" stroke={metric.color} strokeWidth={2} fill={`url(#${gId})`} dot={false}/></AreaChart></ResponsiveContainer></div>;
}

function RingWidget({ metric }) {
  const raw = parseFloat(metric.value);
  const pct = isNaN(raw)?72:raw>1?Math.min(raw,100):raw*100;
  const r=36, circ=Math.PI*2*r, dash=Math.round(circ*pct/100);
  return (
    <div style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:6 }}>
      <div style={{ position:'relative',width:90,height:90 }}>
        <svg viewBox="0 0 88 88" width="90" height="90">
          <circle cx="44" cy="44" r={r} fill="none" stroke={metric.bg} strokeWidth="8"/>
          <circle cx="44" cy="44" r={r} fill="none" stroke={metric.color} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${dash} ${Math.round(circ-dash)}`} transform="rotate(-90 44 44)"/>
        </svg>
        <div style={{ position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center' }}>
          <span style={{ fontSize:17,fontWeight:600,letterSpacing:'-.03em' }}>{Math.round(pct)}%</span>
          <span style={{ fontSize:9,color:'#9f9f98' }}>{metric.label}</span>
        </div>
      </div>
      {metric.delta&&<span style={{ fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:20,background:metric.up?'#f0fdf4':'#fef2f2',color:metric.up?'#16a34a':'#dc2626' }}>{metric.up?'↑':'↓'} {metric.delta}</span>}
    </div>
  );
}

function ComposedWidget({ metric }) {
  const gId = `cgr_${metric.id}`;
  return <div style={{ flex:1,minHeight:0 }}><ResponsiveContainer width="100%" height="100%"><ComposedChart data={getData(metric)} margin={{ top:2,right:4,bottom:0,left:-26 }}><defs><linearGradient id={gId} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={metric.color} stopOpacity={.1}/><stop offset="95%" stopColor={metric.color} stopOpacity={0}/></linearGradient></defs><XAxis dataKey="mes" tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><YAxis tickFormatter={formatMetricNumber} tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><Tooltip content={<Tip/>} cursor={{ fill:'rgba(80,70,228,0.03)' }}/><Bar dataKey="v" fill={metric.color} opacity={.25} radius={[2,2,0,0]}/><Line type="monotone" dataKey="v" stroke={metric.color} strokeWidth={2} dot={false}/></ComposedChart></ResponsiveContainer></div>;
}

function ScatterWidget({ metric }) {
  const data = getData(metric).map((d,i)=>({ x:d.x || i+1, y:d.y || d.v, z:d.z || Math.round(d.v*0.6) }));
  return <div style={{ flex:1,minHeight:0 }}><ResponsiveContainer width="100%" height="100%"><ScatterChart margin={{ top:4,right:4,bottom:0,left:-26 }}><XAxis type="number" dataKey="x" tickFormatter={formatMetricNumber} tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><YAxis type="number" dataKey="y" tickFormatter={formatMetricNumber} tick={{ fill:'#9f9f98',fontSize:9 }} axisLine={false} tickLine={false}/><Tooltip content={<Tip/>} cursor={{ strokeDasharray:'3 3' }}/><Scatter data={data} fill={metric.color} opacity={.7}/></ScatterChart></ResponsiveContainer></div>;
}

function HeatmapWidget({ metric }) {
  const days = ['L','M','X','J','V'];
  const hours = ['9a','12p','3p','6p'];
  const data = days.map(d=>hours.map(h=>({ d,h,v:Math.round(20+Math.random()*80) })));
  return (
    <div style={{ flex:1,display:'flex',flexDirection:'column',justifyContent:'center',gap:3 }}>
      {data.map((row,ri)=>(
        <div key={ri} style={{ display:'flex',gap:3,alignItems:'center' }}>
          <span style={{ fontSize:9,color:'#9f9f98',width:12,textAlign:'right',flexShrink:0 }}>{days[ri]}</span>
          {row.map((cell,ci)=>(
            <div key={ci} style={{ flex:1,height:24,borderRadius:4,background:metric.color,opacity:0.1+cell.v/100*0.9 }} title={`${cell.v}`}/>
          ))}
        </div>
      ))}
      <div style={{ display:'flex',gap:3,paddingLeft:16 }}>
        {hours.map(h=><span key={h} style={{ flex:1,fontSize:9,color:'#9f9f98',textAlign:'center' }}>{h}</span>)}
      </div>
    </div>
  );
}

function FunnelWidget({ metric }) {
  const stages = metric.items || [
    { label:'Visitas',   pct:100, v:'12,400' },
    { label:'Leads',     pct:68,  v:'8,432'  },
    { label:'Prospects', pct:38,  v:'4,712'  },
    { label:'Clientes',  pct:14,  v:'1,736'  },
  ];
  return (
    <div style={{ flex:1,display:'flex',flexDirection:'column',justifyContent:'center',gap:5 }}>
      {stages.map((st,i)=>(
        <div key={i} style={{ display:'flex',alignItems:'center',gap:8 }}>
          <span style={{ fontSize:10,color:'#9f9f98',width:56,textAlign:'right',flexShrink:0 }}>{st.label}</span>
          <div style={{ flex:1,height:20,background:'#f0f0ee',borderRadius:3,overflow:'hidden' }}>
            <div style={{ height:'100%',width:`${st.pct}%`,background:metric.color,opacity:0.4+i*0.15,borderRadius:3,transition:'width .8s' }}/>
          </div>
          <span style={{ fontSize:10,fontWeight:600,color:metric.color,width:38,flexShrink:0 }}>{formatCompactValue(st.v || st.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ListWidget({ metric }) {
  const items = metric.items || (metric.id==='actividad' ? ACTIVITY_LIST : TOP_LIST);
  if (!items?.length) return <SummaryWidget metric={metric} />;

  const isA = metric.id==='actividad';
  return (
    <div style={{ flex:1,overflow:'hidden',display:'flex',flexDirection:'column',justifyContent:'space-between' }}>
      {items.slice(0,4).map((item,i)=>(
        <div key={i} style={{ display:'flex',alignItems:'center',gap:8,paddingBottom:i<3?'7px':0,borderBottom:i<3?'1px solid #f5f5f3':'none' }}>
          {isA ? (
            <div style={{ width:26,height:26,borderRadius:8,flexShrink:0,background:`${item.color}18`,color:item.color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700 }}>{item.initials}</div>
          ) : (
            <span style={{ fontSize:10,color:'#c2c2bb',width:14,textAlign:'center',flexShrink:0 }}>{i+1}</span>
          )}
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontSize:11,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{item.name}</div>
            {isA&&<div style={{ fontSize:10,color:'#9f9f98' }}>{item.action}</div>}
            {!isA&&<div style={{ height:3,background:'#f0f0ee',borderRadius:2,marginTop:3,overflow:'hidden' }}><div style={{ height:'100%',width:`${item.pct}%`,background:metric.color,borderRadius:2 }}/></div>}
          </div>
          <span style={{ fontSize:11,fontWeight:600,color:isA?'#9f9f98':metric.color,flexShrink:0 }}>{isA?item.time:formatCompactValue(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

function MetricLoading() {
  return (
    <div style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:'#6b6b66',background:'#fafaf9',borderRadius:8 }}>
      Cargando métrica...
    </div>
  );
}

function MetricError({ message }) {
  return (
    <div style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4,textAlign:'center',fontSize:12,color:'#dc2626',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:8,padding:12 }}>
      <strong style={{ fontSize:12 }}>Error en métrica</strong>
      <span>{message}</span>
    </div>
  );
}

function MetricNoData() {
  return (
    <div style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:5,textAlign:'center',fontSize:12,color:'#6b6b66',background:'#fafaf9',border:'1px solid #eeeeea',borderRadius:8,padding:12 }}>
      <strong style={{ fontSize:12,color:'#1a1a18' }}>Sin información</strong>
      <span>No hay información para este filtro</span>
    </div>
  );
}

// Metrics withheld on purpose (a filter that cannot be applied yet): not an error, and never a figure
function MetricBlocked({ message }) {
  return (
    <div role="status" data-testid="metric-blocked" style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4,textAlign:'center',fontSize:12,color:'#92400e',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:12 }}>
      <strong style={{ fontSize:12 }}>Filtro no aplicado</strong>
      <span>{message}</span>
    </div>
  );
}

/* Total del período y punto más alto, escritos sobre el gráfico para no depender del mouse */
const CHARTS_WITH_TOTAL = ['bar', 'bar_h', 'line', 'area', 'composed'];

function ChartTotals({ data }) {
  const total = Number(data?.value);
  if (!Number.isFinite(total)) return null;
  const money = data?.unit === 'currency';
  const show = (value) => (money ? `$${formatMoneyTotal(value)}` : formatMetricNumber(value));
  const points = Array.isArray(data?.data) ? data.data.filter((point) => Number.isFinite(Number(point?.value))) : [];
  const peak = points.length > 1 ? points.reduce((best, point) => (Number(point.value) > Number(best.value) ? point : best)) : null;
  return (
    <div data-testid="chart-totals" style={{ display:'flex',flexWrap:'wrap',gap:'2px 14px',alignItems:'baseline',marginBottom:6,flexShrink:0,fontSize:12,color:'#6b6b66' }}>
      <span>Total <strong style={{ fontSize:14,color:'#1a1a18' }}>{show(total)}</strong></span>
      {peak && <span>Más alto <strong style={{ color:'#1a1a18' }}>{show(peak.value)}</strong> ({peak.label})</span>}
    </div>
  );
}

const formatMoneyTotal = (value) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* Lista de totales bajo el gráfico: una fila por grupo o por evento seleccionado */
function TotalsList({ testId, rowPrefix, title, rows }) {
  return (
    <div data-testid={testId} style={{ flexShrink:0,marginTop:8,paddingTop:8,borderTop:'1px solid #eeeeea',display:'flex',flexDirection:'column',gap:4 }}>
      <span style={{ fontSize:10,fontWeight:600,letterSpacing:'.06em',textTransform:'uppercase',color:'#9f9f98' }}>{title}</span>
      {rows.map((row) => (
        <div key={row.id} data-testid={`${rowPrefix}-${row.id}`} style={{ display:'flex',justifyContent:'space-between',gap:8,fontSize:12,color:'#1a1a18' }}>
          <span>{row.name}{row.note && <span style={{ color:'#9f9f98' }}> ({row.note})</span>}</span>
          <strong style={{ fontWeight:600 }}>${formatMoneyTotal(row.value)}</strong>
        </div>
      ))}
    </div>
  );
}

/* Ventas netas de cada grupo de sucursales seleccionado. Una sucursal en dos grupos cuenta en ambos: por eso los grupos
   pueden sumar más que el total general. */
const GroupTotals = ({ groups }) => (
  <TotalsList
    testId="group-totals"
    rowPrefix="group-total"
    title="Por grupo"
    rows={groups.map((group) => ({ id: group.id, name: group.name, value: group.value, note: `${group.branches_count} ${group.branches_count === 1 ? 'sucursal' : 'sucursales'}` }))}
  />
);

/* Ventas netas de cada evento seleccionado. Una orden tiene un solo evento: los eventos suman el total general. */
const EventTotals = ({ events }) => <TotalsList testId="event-totals" rowPrefix="event-total" title="Por evento" rows={events} />;

export default function WidgetRenderer({ metricId, chartType, data, loading, error, noData, blocked, onEdit, editMode, onExport, exporting }) {
  const baseMetric = METRICS.find(m=>m.id===metricId);
  const metric = baseMetric ? resolveMetricData(baseMetric, data) : null;
  if (!metric) return (
    <div style={{ display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:6,color:'#9f9f98',cursor:'pointer' }} onClick={onEdit}>
      <div style={{ fontSize:22,opacity:.4 }}>+</div>
      <div style={{ fontSize:11 }}>Sin configurar</div>
    </div>
  );

  const canRenderChart = !loading && !error && !noData && !blocked;

  return (
    <div style={{ display:'flex',flexDirection:'column',height:'100%' }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,flexShrink:0 }}>
        <div style={{ display:'flex',alignItems:'center',gap:6 }}>
          <div style={{ width:7,height:7,borderRadius:'50%',background:metric.color,flexShrink:0 }}/>
          <span style={{ fontSize:10,fontWeight:600,letterSpacing:'.06em',textTransform:'uppercase',color:'#9f9f98' }}>{metric.label}</span>
        </div>
        {editMode&&onEdit&&(
          <button onClick={onEdit} title="Editar widget" style={{ background:'transparent',border:'1px solid #e8e8e4',borderRadius:5,width:24,height:24,cursor:'pointer',fontSize:11,color:'#9f9f98',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>✎</button>
        )}
      </div>
      {blocked && <MetricBlocked message={blocked} />}
      {!blocked && loading && <MetricLoading />}
      {!blocked && !loading && error && <MetricError message={error} />}
      {!blocked && !loading && !error && noData && <MetricNoData />}
      {canRenderChart && CHARTS_WITH_TOTAL.includes(chartType) && <ChartTotals data={data}/>}
      {canRenderChart && chartType==='table'      && <ConsolidatedTable entry={data} onExport={onExport} exporting={exporting}/>}
      {canRenderChart && chartType==='kpi'        && <KpiWidget      metric={metric}/>}
      {canRenderChart && chartType==='stat_delta' && <StatDeltaWidget metric={metric}/>}
      {canRenderChart && chartType==='bar'        && <BarWidget       metric={metric}/>}
      {canRenderChart && chartType==='bar_h'      && <BarHWidget      metric={metric} growWithLists={hasBreakdownLists(metricId, data)}/>}
      {canRenderChart && chartType==='line'       && <LineWidget      metric={metric}/>}
      {canRenderChart && chartType==='area'       && <AreaWidget      metric={metric}/>}
      {canRenderChart && chartType==='ring'       && <RingWidget      metric={metric}/>}
      {canRenderChart && chartType==='composed'   && <ComposedWidget  metric={metric}/>}
      {canRenderChart && chartType==='scatter'    && <ScatterWidget   metric={metric}/>}
      {canRenderChart && chartType==='heatmap'    && <HeatmapWidget   metric={metric}/>}
      {canRenderChart && chartType==='funnel'     && <FunnelWidget    metric={metric}/>}
      {canRenderChart && chartType==='list'       && <ListWidget      metric={metric}/>}
      {canRenderChart && metricId==='ventas_por_sucursal' && Array.isArray(data?.groups) && data.groups.length > 0 && <GroupTotals groups={data.groups}/>}
      {canRenderChart && metricId==='ventas_por_sucursal' && Array.isArray(data?.events) && data.events.length > 0 && <EventTotals events={data.events}/>}
    </div>
  );
}
